import type { KyResponse } from 'ky';
import { http } from '@/lib/http';
import { IntegrationModeError, modeErrorCode } from './binding-mode';
import { IntegrationToggleError } from './integration-toggle';
import { canonicalCategory } from './legacy-category';
import { RoutingSaveError, refusalReason } from './provider-routing';
import type {
  AirlineProviderState,
  AirlineProviderSwitchResult,
  FlightDataRefreshResult,
  FlightDataStatus,
  IntegrationDescriptor,
  IntegrationMode,
  IntegrationProbeOutcome,
  IntegrationProbeResult,
  IntegrationToggleErrorCode,
  ProvidersIntegrations,
  ProvidersRouting,
  ProvidersStatus,
  RoutingSaveBody,
  RoutingSaveErrorCode,
  RoutingView,
} from './types';

/** One-shot read of live provider health (seeds the page; WS pushes thereafter). */
export async function fetchProvidersStatus(): Promise<ProvidersStatus> {
  return http.get('v1/providers/status').json<ProvidersStatus>();
}

/** Flight-data (FlightAware/AeroAPI) freshness for the providers card. */
export async function fetchFlightData(): Promise<FlightDataStatus> {
  return http.get('v1/providers/flight-data').json<FlightDataStatus>();
}

/** Operator-forced AeroAPI re-pull (demo prep). The predictor walks pages
 *  upstream (up to ~60s), so this call gets a long timeout of its own. */
export async function refreshFlightData(): Promise<FlightDataRefreshResult> {
  return http
    .post('v1/providers/flight-data/refresh', { timeout: 120_000 })
    .json<FlightDataRefreshResult>();
}

/**
 * What this instance is actually connected to (slice E2) — the E1 seam
 * `GET /v1/providers/integrations` — with each row's availability (`health`,
 * #840), which the BFF computes on this very read. That can take a few seconds
 * (the BFF waits up to ~3.5s for its checks on top of the inventory read), so
 * the call gets a longer timeout than ky's 10s default.
 */
export async function fetchIntegrations(): Promise<ProvidersIntegrations> {
  return http.get('v1/providers/integrations', { timeout: 15_000 }).json<ProvidersIntegrations>();
}

/**
 * One on-demand connection test, routed by the BFF to the app that owns the
 * credential. THE single client for `POST /v1/providers/{vendor}/test` on this
 * page — the shipped diagnostics card codes against the same wire shape.
 *
 * A 429 (`test_rate_limited`) is a friendly outcome, not an error, and a failed
 * probe is a 200 with `ok:false` — never a 500. Only a transport failure throws.
 */
export async function probeIntegration(vendor: string): Promise<IntegrationProbeOutcome> {
  // The chain can spend two live vendor steps at ~10s each, and every router in
  // the chain outlasts the one it waits on (BFF 15s > integrations 12s > the
  // declaring app's per-step budget). ky's default 10s would surface a spurious
  // client error while the vendor call is still in flight.
  const res = await http.post(`v1/providers/${vendor}/test`, {
    throwHttpErrors: false,
    timeout: 25_000,
  });
  if (res.status === 429) {
    const body = await res
      .json<{ error?: string; retryAfterMs?: number }>()
      .catch(() => ({}) as { retryAfterMs?: number });
    return { kind: 'rate_limited', retryAfterMs: body.retryAfterMs };
  }
  if (!res.ok) throw new Error(`vendor probe failed (HTTP ${res.status})`);
  return { kind: 'result', result: await res.json<IntegrationProbeResult>() };
}

const TOGGLE_ERROR_CODES: readonly IntegrationToggleErrorCode[] = [
  'credentials_missing',
  'last_provider',
  'not_toggleable',
  'version_conflict',
  'unknown_vendor',
  'invalid_request',
];

/**
 * #839 — switch one integration on or off. The answer IS the row as the server
 * committed it (effective `enabled` + `toggle`); nothing on screen changes until
 * it arrives. A refusal throws {@link IntegrationToggleError} with the reason.
 * The body names the binding by its SERVICE: the control plane reads a legacy
 * spelling as its service anyway, and the BFF wire stops accepting it (M3 B3).
 */
export async function toggleIntegration(
  descriptor: Pick<IntegrationDescriptor, 'vendor' | 'category'>,
  enabled: boolean,
  reason: string,
): Promise<IntegrationDescriptor> {
  const res = await http.post(`v1/providers/integrations/${descriptor.vendor}/toggle`, {
    json: { enabled, reason, category: canonicalCategory(descriptor.category) },
    throwHttpErrors: false,
  });
  if (res.ok) return res.json<IntegrationDescriptor>();
  throw await toggleError(res);
}

/**
 * The airline provider's card (`airline-air-ticketing.v1` §0): connected or not, the mode, and
 * each capability — available, or why not. Fail-soft at the BFF: `reachable: false` when it
 * could not ask the airline (unknown, never "not connected").
 */
export async function fetchAirlineProvider(): Promise<AirlineProviderState> {
  return http.get('v1/providers/airline').json<AirlineProviderState>();
}

/**
 * The airline provider's MASTER switch: the per-service toggle applied to its three services,
 * with one reason. The answer lists each service's outcome (a refusal of one does not undo the
 * others); when nothing committed it throws {@link IntegrationToggleError} like a single toggle.
 */
export async function switchAirlineProvider(enabled: boolean, reason: string): Promise<AirlineProviderSwitchResult> {
  const res = await http.post('v1/providers/airline/switch', {
    json: { enabled, reason },
    throwHttpErrors: false,
    // Up to three control-plane writes in a row (8s budget each at the BFF).
    timeout: 30_000,
  });
  if (res.ok) return res.json<AirlineProviderSwitchResult>();
  throw await toggleError(res);
}

/** A refused toggle answer as {@link IntegrationToggleError} (an unknown code reads `unavailable`). */
async function toggleError(res: KyResponse): Promise<IntegrationToggleError> {
  const body = await res.json<{ error?: string }>().catch(() => ({}) as { error?: string });
  const code = (TOGGLE_ERROR_CODES as readonly string[]).includes(body.error ?? '')
    ? (body.error as IntegrationToggleErrorCode)
    : 'unavailable';
  return new IntegrationToggleError(code, res.status);
}

/**
 * M2 (D14–D16) — choose the mode one binding serves its NEW operations in
 * (`POST /v1/providers/integrations/{vendor}/mode`, body `{ category, mode, reason }`).
 * Like the switch: the answer IS the row as committed (`{ integration }`), and
 * nothing on screen moves until it arrives. A refusal throws
 * {@link IntegrationModeError} with the 409 reason (or 403 / 400 / unavailable).
 * Like the switch, the body names the binding by its SERVICE.
 */
export async function setIntegrationMode(
  descriptor: Pick<IntegrationDescriptor, 'vendor' | 'category'>,
  mode: IntegrationMode,
  reason: string,
): Promise<IntegrationDescriptor> {
  const res = await http.post(`v1/providers/integrations/${descriptor.vendor}/mode`, {
    json: { category: canonicalCategory(descriptor.category), mode, reason },
    throwHttpErrors: false,
  });
  type Answer = { integration?: IntegrationDescriptor; reason?: unknown; error?: unknown };
  const body = await res.json<Answer>().catch((): Answer => ({}));
  if (res.ok && body.integration) return body.integration;
  throw new IntegrationModeError(res.ok ? 'unavailable' : modeErrorCode(res.status, body), res.status);
}


/**
 * §14.5 — the "Orden de uso" of every routed key. Fail-closed upstream: an
 * unreachable control plane is an error here, and the panels simply do not show.
 */
export async function fetchRouting(): Promise<ProvidersRouting> {
  return http.get('v1/providers/routing', { timeout: 15_000 }).json<ProvidersRouting>();
}

/**
 * §14.5 — save one key's order / strategy / participation. The answer IS the
 * view as committed; a refusal throws {@link RoutingSaveError} with its code
 * (409 `version_conflict` means someone else saved first: reload).
 */
export async function saveRouting(
  view: Pick<RoutingView, 'category' | 'capability'>,
  body: RoutingSaveBody,
): Promise<RoutingView> {
  const res = await http.put(`v1/providers/routing/${view.category}/${view.capability}`, {
    json: body,
    throwHttpErrors: false,
  });
  if (res.ok) return res.json<RoutingView>();
  const code: RoutingSaveErrorCode =
    res.status === 409
      ? 'version_conflict'
      : res.status === 400
        ? 'invalid_request'
        : res.status === 403
          ? 'forbidden'
          : 'unavailable';
  // A 400 may say WHY (the airline always wins); only a reason the console knows is kept.
  type Refusal = { reason?: unknown };
  const refusal: Refusal = res.status === 400 ? await res.json<Refusal>().catch((): Refusal => ({})) : {};
  throw new RoutingSaveError(code, res.status, refusalReason(refusal.reason));
}
